import { Automation, Shop, Tag, TagsDocument } from "@/types/v2";
import {
  DEFAULT_TAGS,
  NEW_SHOP_CARD_COLORS,
  NEW_SHOP_CARD_CONTENTS,
  NEW_SHOP_INBOX_PANEL_CONFIG,
} from "@/lib/const";

function valuesEqual(expected: unknown, actual: unknown): boolean {
  if (Array.isArray(expected)) {
    return (
      Array.isArray(actual) &&
      JSON.stringify(expected) === JSON.stringify(actual)
    );
  }
  return expected === actual;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const EXPECTED_SHOP_CONFIG = {
  dashboardScaling: "scaleToFit" as const,
  useShopHeroEmployeeJobTitle: true,
  showContainerLinkIcons: false,
  inboxPanel: NEW_SHOP_INBOX_PANEL_CONFIG,
};

const REQUIRED_ENABLED_FEATURES = [
  "isPromiseTimeEnabled",
  "isTasksTabEnabled",
  "isDispatchViewEnabled",
] as const;

// Mirrors functions-v2/src/lib/defaultAutomations.ts's buildDefaultAutomationDocuments
// names/order exactly. Frontend code cannot import functions-v2 (server-only
// deps), so this list is kept in sync by convention — same as
// NEW_SHOP_CARD_COLOR_LEGEND's own "keep in sync" comment in src/lib/const.ts,
// which defaultAutomations.ts depends on for its color lookups.
export const DEFAULT_AUTOMATION_NAMES = [
  "Reset Automations",
  "Waiter Is Color",
  "Waiter Change Color",
  "Drop-off Change Color",
  "Waiter Is Tag",
  "Waiter Change Tag",
  "Drop-off Change Tag",
  "Promise Time Priority / Is Today",
  "Promise Time Changes / Is NOT Today",
  "Container Alert",
] as const;

// ---------------------------------------------------------------------------
// Per-field audit contract (WEBV3-289 design alignment). Every audited field
// yields a row with a status, so the UI can show matches and "Not set" states,
// not only drifts.
// ---------------------------------------------------------------------------

export type AuditStatus = "missing" | "drift" | "match";

export type SettingRow = {
  path: string;
  label: string;
  group: string;
  expected: unknown;
  actual: unknown;
  status: AuditStatus;
  // Card color rows only: hex swatches for the default and the shop's value.
  swatch?: { expected: string; actual?: string };
};

export type SettingsAuditResult = { rows: SettingRow[] };

export type ListAuditResult = {
  expectedCount: number;
  present: string[];
  missing: string[];
  extra: string[];
};

export function humanizeKey(key: string): string {
  const spaced = key
    .replace(/-(\d+)$/, " $1")
    .replace(/([A-Z])/g, " $1")
    .toLowerCase()
    .trim()
    .replace(/^is /, "")
    .replace(/ enabled$/, "");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function statusOf(expected: unknown, actual: unknown): AuditStatus {
  if (actual === undefined) return "missing";
  return valuesEqual(expected, actual) ? "match" : "drift";
}

function auditAgainstBaseline(
  expected: Record<string, unknown>,
  actual: Record<string, unknown> | undefined,
  prefix: string
): SettingRow[] {
  const rows: SettingRow[] = [];
  for (const [key, expectedValue] of Object.entries(expected)) {
    const path = `${prefix}.${key}`;
    const actualValue = actual?.[key];
    if (isPlainObject(expectedValue)) {
      rows.push(
        ...auditAgainstBaseline(
          expectedValue,
          isPlainObject(actualValue) ? actualValue : undefined,
          path
        )
      );
    } else {
      rows.push({
        path,
        label: humanizeKey(key),
        group: prefix,
        expected: expectedValue,
        actual: actualValue,
        status: statusOf(expectedValue, actualValue),
      });
    }
  }
  return rows;
}

// Covers both shop.shopConfig (WEBV3-286's scalar onboarding toggles) and
// shop.cardConfig.cardContents (WEBV3-190/286's per-card display defaults)
// as one audit category, since both were part of the same "settings toggled
// by hand on every onboarding" pitch list.
export function auditShopConfig(shop: Shop): SettingsAuditResult {
  return {
    rows: [
      ...auditAgainstBaseline(
        EXPECTED_SHOP_CONFIG as unknown as Record<string, unknown>,
        shop.shopConfig as Record<string, unknown> | undefined,
        "shopConfig"
      ),
      ...auditAgainstBaseline(
        NEW_SHOP_CARD_CONTENTS as unknown as Record<string, unknown>,
        shop.cardConfig?.cardContents as Record<string, unknown> | undefined,
        "cardConfig.cardContents"
      ),
    ],
  };
}

export function auditEnabledFeatures(shop: Shop): SettingsAuditResult {
  return {
    rows: REQUIRED_ENABLED_FEATURES.map((flag): SettingRow => {
      const actual = shop.enabledFeatures?.[flag];
      return {
        path: `enabledFeatures.${flag}`,
        label: humanizeKey(flag),
        group: "",
        expected: true,
        actual,
        status: statusOf(true, actual),
      };
    }),
  };
}

export function auditCardColors(shop: Shop): SettingsAuditResult {
  const actualById = new Map(
    (shop.cardConfig?.cardColors ?? []).map((c) => [c.id, c] as const)
  );
  return {
    rows: NEW_SHOP_CARD_COLORS.map((expected): SettingRow => {
      const actual = actualById.get(expected.id);
      const status: AuditStatus = !actual
        ? "missing"
        : actual.title === expected.title && actual.color === expected.color
          ? "match"
          : "drift";
      return {
        path: `cardColors.${expected.id}`,
        label: humanizeKey(expected.id),
        group: "",
        expected: expected.title,
        actual: actual?.title,
        status,
        swatch: { expected: expected.color, actual: actual?.color },
      };
    }),
  };
}

function auditList(
  defaults: readonly string[],
  actualLabels: string[],
  normalize: (label: string) => string
): ListAuditResult {
  const actualSet = new Set(actualLabels.map(normalize));
  const defaultSet = new Set(defaults.map(normalize));
  return {
    expectedCount: defaults.length,
    present: defaults.filter((label) => actualSet.has(normalize(label))),
    missing: defaults.filter((label) => !actualSet.has(normalize(label))),
    extra: actualLabels.filter((label) => !defaultSet.has(normalize(label))),
  };
}

export function auditTags(tagsDoc: TagsDocument | undefined): ListAuditResult {
  return auditList(
    DEFAULT_TAGS,
    (tagsDoc?.tags ?? []).map((t: Tag) => t.label),
    (label) => label.toLowerCase()
  );
}

export function auditAutomations(automations: Automation[]): ListAuditResult {
  return auditList(
    DEFAULT_AUTOMATION_NAMES,
    automations
      .map((a) => a.name)
      .filter((name): name is string => name !== undefined),
    (name) => name
  );
}
